import { useTranslation } from "@next-platform/client-common-core";
import { RulebookListCardStatus } from "@next-platform/client-common-core/rulebook/cards/RulebookListCardStatus";
import {
  Badge,
  BadgeSize,
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  DropDownMenu,
  type DropDownMenuItem,
  Icon,
  IconSize,
  IconVariants,
  Image,
  Shape,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import type { RulebookMeterChip, RulebookModerationFix } from "@next-platform/packages-ts-types";
import cx from "classnames";
import type { KeyboardEvent, MouseEvent } from "react";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";

import RulebookListCardModerationRow from "./RulebookListCardModerationRow";
import RulebookListCardStats from "./RulebookListCardStats";
import RulebookMeterChipBadge from "./RulebookMeterChipBadge";

export type RulebookListCardMode = "owner" | "public";

export type RulebookListCardProps = {
  id: string;
  name: string;
  description?: string;
  coverUrl?: string;
  isCoverBlurred?: boolean;
  meterChips: RulebookMeterChip[];
  numHiddenMeters: number;
  numRules: number;
  numBots: number;
  numPersonas: number;
  /** Public cards only: renders the "Definition visible" chip when the author shares the definition. */
  isDefinitionVisible?: boolean;
  status?: RulebookListCardStatus;
  moderationFix?: RulebookModerationFix;
  mode: RulebookListCardMode;
  creatorUsername?: string;
  creatorPath?: string;
  menuActions?: DropDownMenuItem[];
  onOpen?: () => void;
  isSelected?: boolean;
  "data-testid"?: string;
};

type RulebookListCardRenderProps = Pick<
  RulebookListCardProps,
  | "id"
  | "description"
  | "coverUrl"
  | "numHiddenMeters"
  | "numRules"
  | "numBots"
  | "numPersonas"
  | "isDefinitionVisible"
  | "status"
  | "moderationFix"
  | "mode"
  | "creatorUsername"
  | "creatorPath"
  | "menuActions"
  | "onOpen"
  | "isSelected"
> & {
  cardName: string;
  cardClassName: string;
  creatorTestId: string;
  hiddenTitle: string;
  hiddenValues: { count: number };
  imageClassName: string;
  imagePlaceholder: React.ReactNode;
  meterChipBadges: React.ReactNode;
  menuLabel: string;
  testId: string;
  handleImageError: () => void;
  handleCreatorClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  handleOpen: () => void;
  stopMenuEvent: (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void;
  hasImage: boolean;
};

const renderRulebookListCardHiddenMetersChip = ({ hiddenTitle, hiddenValues }: Pick<RulebookListCardRenderProps, "hiddenTitle" | "hiddenValues">) => (
  <Badge
    showDot={false}
    size={BadgeSize.SMALL}
    className="inline-flex h-[22px] gap-1 rounded-[6px] border border-dashed border-gray-7 bg-transparent px-2 text-gray-10"
    title={hiddenTitle}
    data-testid="RulebookListCard-HiddenMeters"
  >
    <Icon icon={IconVariants.EYE_OFF} size={IconSize.XSMALL} />
    <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR}>
      <Trans i18nKey="rulebook:card.hiddenMeters" defaults="+{{count}} hidden" values={hiddenValues} />
    </Text>
  </Badge>
);

const renderRulebookListCardDescription = ({ description, mode }: Pick<RulebookListCardProps, "description" | "mode">) => {
  if (description) {
    return (
      <Text
        as="p"
        variant={TextVariants.LABEL_MEDIUM_REGULAR}
        className="line-clamp-3 leading-[1.45] text-gray-11"
        title={description}
        data-testid="RulebookListCard-Description"
      >
        {description}
      </Text>
    );
  }

  if (mode === "owner") {
    return (
      <Text as="p" variant={TextVariants.LABEL_MEDIUM_REGULAR} className="leading-[1.45] text-gray-10" data-testid="RulebookListCard-AddDescription">
        <Trans i18nKey="rulebook:card.addDescription" defaults="Add description" />
      </Text>
    );
  }

  return null;
};

type RulebookListCardCreatorProps = {
  creatorPath?: string;
  creatorTestId: string;
  creatorUsername?: string;
  handleCreatorClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  testId: string;
};

const renderRulebookListCardCreator = ({ creatorPath, creatorTestId, creatorUsername, handleCreatorClick, testId }: RulebookListCardCreatorProps) => {
  if (!creatorUsername) {
    return null;
  }

  if (creatorPath) {
    return (
      <LocalizedLink
        to={creatorPath}
        className="min-w-0 max-w-full text-gray-10 hover:underline"
        onClick={handleCreatorClick}
        data-testid={`${testId}-CreatorLink`}
      >
        <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR}>
          @{creatorUsername}
        </Text>
      </LocalizedLink>
    );
  }

  return (
    <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10" data-testid={creatorTestId}>
      @{creatorUsername}
    </Text>
  );
};

const renderRulebookListCardStats = ({
  status,
  numRules,
  numBots,
  numPersonas,
  isDefinitionVisible,
  mode,
}: Pick<RulebookListCardProps, "status" | "numRules" | "numBots" | "numPersonas" | "isDefinitionVisible" | "mode">) => (
  <RulebookListCardStats
    status={status}
    numRules={numRules}
    numBots={numBots}
    numPersonas={numPersonas}
    isDefinitionVisible={mode === "public" && isDefinitionVisible}
  />
);

const renderRulebookListCard = ({
  id,
  description,
  coverUrl,
  numHiddenMeters,
  numRules,
  numBots,
  numPersonas,
  isDefinitionVisible,
  status,
  moderationFix,
  mode,
  creatorUsername,
  creatorPath,
  menuActions,
  onOpen,
  isSelected,
  cardName,
  cardClassName,
  creatorTestId,
  hiddenTitle,
  hiddenValues,
  imageClassName,
  imagePlaceholder,
  meterChipBadges,
  menuLabel,
  testId,
  handleImageError,
  handleCreatorClick,
  handleOpen,
  stopMenuEvent,
  hasImage,
}: RulebookListCardRenderProps) => {
  const cover = hasImage ? (
    <Image src={coverUrl} alt={cardName} className={imageClassName} onError={handleImageError} data-testid="RulebookListCard-Cover" />
  ) : (
    imagePlaceholder
  );
  const descriptionContent = renderRulebookListCardDescription({ description, mode });
  const creatorContent = renderRulebookListCardCreator({ creatorPath, creatorTestId, creatorUsername, handleCreatorClick, testId });
  const hiddenMetersChip = numHiddenMeters > 0 ? renderRulebookListCardHiddenMetersChip({ hiddenTitle, hiddenValues }) : null;

  return (
    <article className={cardClassName} onClick={onOpen ? handleOpen : undefined} data-rulebook-id={id} data-testid={testId}>
      <div className="m-2 mr-0 w-[104px] flex-none self-start aspect-[640/832] overflow-hidden rounded-[10px] sm:w-[132px]">{cover}</div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-3 pr-10">
        <div className="min-w-0">
          {onOpen ? (
            <button
              type="button"
              className="m-0 block w-full cursor-pointer border-0 bg-transparent p-0 text-left text-inherit"
              title={cardName}
              aria-pressed={isSelected}
              data-testid={`${testId}-OpenButton`}
            >
              <Text as="span" variant={TextVariants.LABEL_LARGE_BOLD} className="block truncate text-gray-12" data-testid="RulebookListCard-Name">
                {cardName}
              </Text>
            </button>
          ) : (
            <Text
              as="span"
              variant={TextVariants.LABEL_LARGE_BOLD}
              className="block truncate text-gray-12"
              title={cardName}
              data-testid="RulebookListCard-Name"
            >
              {cardName}
            </Text>
          )}
          {creatorContent}
        </div>
        {descriptionContent}
        <div className="flex flex-wrap gap-1" data-testid="RulebookListCard-MeterChips">
          {meterChipBadges}
          {hiddenMetersChip}
        </div>
        {renderRulebookListCardStats({ status, numRules, numBots, numPersonas, isDefinitionVisible, mode })}
        {mode === "owner" && moderationFix ? <RulebookListCardModerationRow moderationFix={moderationFix} /> : null}
      </div>
      {menuActions?.length ? (
        <div className="absolute right-1.5 top-2" onClick={stopMenuEvent} onKeyDown={stopMenuEvent} data-testid="RulebookListCard-Menu">
          <DropDownMenu listItems={menuActions} isPortaled>
            <ButtonIcon
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SMALL_PLUS}
              shape={Shape.PILL}
              icon={IconVariants.MORE_VERTICAL}
              aria-label={menuLabel}
              className="text-gray-11 hover:bg-gray-5"
              data-testid="RulebookListCard-MenuButton"
            />
          </DropDownMenu>
        </div>
      ) : null}
    </article>
  );
};

type CardClassArgs = { status?: RulebookListCardStatus; isSelected?: boolean; isOpenable: boolean };

/** Selected wins over Needs changes, which wins over the neutral (hoverable when the card opens) border. */
const resolveCardBorderClass = ({ status, isSelected, isOpenable }: CardClassArgs) => {
  if (isSelected) {
    return "border-blue-10";
  }
  if (status === RulebookListCardStatus.NEEDS_CHANGES) {
    return "border-red-9";
  }
  return cx("border-gray-6", isOpenable ? "hover:border-gray-7/50 dark:hover:border-gray-8" : "");
};

const resolveCardClassName = (args: CardClassArgs) =>
  cx(
    "relative flex w-full rounded-[14px] border border-solid bg-gray-3 text-left text-gray-12 dark:text-white",
    args.isOpenable ? "cursor-pointer hover:bg-gray-4" : "",
    resolveCardBorderClass(args),
  );

const RulebookListCard = ({
  id,
  name,
  description,
  coverUrl,
  isCoverBlurred = false,
  meterChips,
  numHiddenMeters,
  numRules,
  numBots,
  numPersonas,
  isDefinitionVisible = false,
  status,
  moderationFix,
  mode,
  creatorUsername,
  creatorPath,
  menuActions,
  onOpen,
  isSelected,
  "data-testid": testId = "RulebookListCard",
}: RulebookListCardProps) => {
  const { t: translate } = useTranslation();
  const [stateIsImageError, setStateIsImageError] = useState(false);
  const handleImageError = useCallback(() => setStateIsImageError(true), []);
  useEffect(() => setStateIsImageError(false), [coverUrl]);
  const handleOpen = useCallback(() => onOpen?.(), [onOpen]);
  const handleCreatorClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation(), []);
  const stopMenuEvent = useCallback((event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);
  const hasImage = Boolean(coverUrl && !stateIsImageError);
  const hiddenTitle = useMemo(
    () =>
      translate(
        "rulebook:card.hiddenMetersHint",
        numHiddenMeters === 1
          ? "{{count}} hidden meter: tracked and used by rules, but not shown in chat"
          : "{{count}} hidden meters: tracked and used by rules, but not shown in chat",
        { count: numHiddenMeters },
      ),
    [translate, numHiddenMeters],
  );
  const hiddenValues = useMemo(() => ({ count: numHiddenMeters }), [numHiddenMeters]);
  const meterChipBadges = useMemo(
    () => meterChips.map((chip, index) => <RulebookMeterChipBadge key={`${chip.label}-${index}`} chip={chip} index={index} />),
    [meterChips],
  );
  const cardClassName = resolveCardClassName({ status, isSelected, isOpenable: Boolean(onOpen) });
  const imageClassName = cx("size-full object-cover object-top", isCoverBlurred ? "blur-md" : "");
  const imagePlaceholder = (
    <div className="flex size-full flex-col items-center justify-center gap-1 bg-gray-4 text-gray-10" data-testid="RulebookListCard-NoCover">
      <Icon icon={IconVariants.IMAGE} size={IconSize.MEDIUM} />
      {mode === "owner" ? <Trans i18nKey="rulebook:card.noCover" defaults="No cover" /> : null}
    </div>
  );
  const cardName = name.trim() ? name : translate("rulebook:card.untitled", "Untitled Rulebook");
  const creatorTestId = `${testId}-Creator`;

  return renderRulebookListCard({
    id,
    description,
    coverUrl,
    numHiddenMeters,
    numRules,
    numBots,
    numPersonas,
    isDefinitionVisible,
    status,
    moderationFix,
    mode,
    creatorUsername,
    creatorPath,
    menuActions,
    onOpen,
    isSelected,
    cardName,
    cardClassName,
    creatorTestId,
    hiddenTitle,
    hiddenValues,
    imageClassName,
    imagePlaceholder,
    meterChipBadges,
    menuLabel: translate("rulebook:card.actions.more", "More options"),
    testId,
    handleImageError,
    handleCreatorClick,
    handleOpen,
    stopMenuEvent,
    hasImage,
  });
};

export default memo(RulebookListCard);
